/**
 * Sites de uma empresa: a chave que o script manda e as origens que podem mandar.
 *
 * A chave é global, não por empresa: é ela que a função de captação recebe em
 * `site` e usa para descobrir de quem é o lead. Por isso validação apertada e
 * checagem de duplicidade antes de gravar.
 *
 * As origens são o que autoriza o CORS. Origem é esquema + domínio + porta, sem
 * caminho. Deixar um caminho passar daria falsa sensação de restrição, porque o
 * navegador manda só a origem no cabeçalho.
 */

export const ENDPOINT = 'https://southamerica-east1-leads-produxia.cloudfunctions.net/receberLead';

const CHAVE_OK = /^[a-z0-9][a-z0-9-]{1,39}$/;

export function chaveValida(chave) {
  return CHAVE_OK.test(String(chave || ''));
}

/** Sugere uma chave a partir do domínio: www.casa-perini.com.br → casa-perini */
export function chaveSugerida(dominio) {
  const limpo = String(dominio || '')
    .trim().toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .split('/')[0]
    .split('.')[0]
    .replace(/[^a-z0-9-]/g, '');
  return CHAVE_OK.test(limpo) ? limpo : '';
}

/**
 * Normaliza uma origem digitada. Devolve null se não der para aproveitar.
 * Aceita "maximizy.com.br" e completa com https, que é o caso normal.
 */
export function normalizarOrigem(bruta) {
  let t = String(bruta || '').trim();
  if (!t) return null;
  if (!/^https?:\/\//i.test(t)) t = 'https://' + t;
  let u;
  try { u = new URL(t); } catch { return null; }
  if (u.protocol !== 'https:' && u.protocol !== 'http:') return null;
  if (!u.hostname || !u.hostname.includes('.')) return null;
  // localhost fica de fora de propósito: origem de teste não entra em produção.
  return u.origin;
}

/** Limpa a lista inteira, tirando repetido e o que não dá para aproveitar. */
export function normalizarOrigens(lista) {
  const vistas = new Set();
  const boas = [];
  const ruins = [];
  for (const bruta of lista || []) {
    if (!String(bruta || '').trim()) continue;
    const o = normalizarOrigem(bruta);
    if (!o) { ruins.push(String(bruta).trim()); continue; }
    if (vistas.has(o)) continue;
    vistas.add(o); boas.push(o);
  }
  return { origens: boas, invalidas: ruins };
}

export const SCRIPT = 'https://leads.maximizy.com.br/mx.js';

/**
 * Rastreio de jornada no Tag Manager.
 *
 * Carrega o script por código em vez de colar a tag pronta, por dois motivos
 * medidos no ar: o Tag Manager RECRIA a tag copiando só atributos conhecidos,
 * então `data-site` é descartado e o script fica sem saber de quem é; e o
 * `async` também some, o que faria o script travar o desenho da página do
 * cliente. Criando o elemento aqui, as duas coisas ficam sob controle.
 */
export function trechoInstalacao(chave) {
  return `<script>\n(function (d) {\n  var s = d.createElement('script');\n`
       + `  s.src = '${SCRIPT}?site=${chave}';\n  s.async = true;\n`
       + `  d.head.appendChild(s);\n})(document);\n<\/script>`;
}

/** Fora do Tag Manager, direto no HTML do site, uma linha resolve. */
export function trechoDireto(chave) {
  return `<script src="${SCRIPT}?site=${chave}" async></script>`;
}

/**
 * Captação de formulário: continua sendo o snippet grande, que precisa saber
 * ler os campos de cada construtor de site. Daqui só sai a parte que muda.
 */
export function trechoFormulario(chave) {
  return `var ENDPOINT = '${ENDPOINT}';\nvar SITE = '${chave}';`;
}

/** Checa o formulário inteiro e devolve a primeira reclamação, ou null. */
export function validarSite({ chave, rotulo, origens }, chavesEmUso = []) {
  if (!chaveValida(chave)) {
    return 'A chave deve ter de 2 a 40 caracteres, só letras minúsculas, números e hífen.';
  }
  if (chavesEmUso.includes(chave)) return `A chave "${chave}" já está em uso.`;
  if (!String(rotulo || '').trim()) return 'Dê um nome ao site, é o que aparece na nota do lead.';
  if (!origens || !origens.length) return 'Informe ao menos uma origem, senão o site não consegue enviar.';
  return null;
}
